import { CustomError, getEnv } from "@umami/utils";
import { differenceInDays, format, formatDistance } from "date-fns";
import { ethers, formatUnits } from "ethers";

import { type ChainFullId, OperationTypes, TransactionDirections } from "../consts";
import {
  type MultichainNFTBalance,
  type MultichainTokenBalance,
  type OperationType,
  type Transaction,
  type TransactionDirection,
} from "../types";
import {
  type AddressNFTInstance,
  type TokenBalance,
  type TokenTransfer,
  type TransactionResponse,
} from "./api-types";

export const DEFAULT_ETHERS_DECIMALS = 18;

export const isEvmAddressValid = (address: string) => ethers.isAddress(address);

export const formatEthersAmount = (
  amount: string | number,
  decimals: number = DEFAULT_ETHERS_DECIMALS
) => {
  const value = formatUnits(amount, decimals);
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });

  return formatter.format(Number(value));
};

export const transactionTypes = {
  token_transfer: OperationTypes.TOKEN_TRANSFER,
  coin_transfer: OperationTypes.TRANSACTION,
  contract_call: OperationTypes.CONTRACT_CALL,
};

export const normalizeTimestamp = (
  timestamp: string = new Date().toISOString(),
  { isShort = false }: { isShort?: boolean } = {}
): string => {
  const currentDate = new Date();
  const timestampDate = new Date(timestamp);

  const dayDifference = differenceInDays(currentDate, timestamp);
  const isSameYear = currentDate.getFullYear() === timestampDate.getFullYear();

  if (isShort) {
    if (dayDifference < 1) {
      return format(timestampDate, "HH:mm");
    }

    return format(timestampDate, "dd/MM");
  }

  if (dayDifference < 2) {
    return formatDistance(timestamp, currentDate, { addSuffix: true });
  } else if (isSameYear) {
    return format(timestamp, "dd MMM");
  } else {
    return format(timestamp, "dd MMM yyyy");
  }
};

export const getTransactionDirection = (
  transaction: TransactionResponse | TokenTransfer,
  address: string
): TransactionDirection => {
  if (transaction.from.hash === address) {
    return TransactionDirections.SEND;
  } else if (transaction.to.hash === address) {
    return TransactionDirections.RECEIVE;
  }

  return TransactionDirections.SEND;
};

export const parseEvmAddress = (address: string) => {
  if (!ethers.isAddress(address)) {
    throw new CustomError(`Invalid evm address: ${address}`);
  }

  return { type: "implicit", pkh: address } as const;
};

export const normalizeEvmTx =
  (chainId: ChainFullId, { explorerUrl, symbol }: { explorerUrl: string; symbol: string }) =>
  (tx: TransactionResponse, address: string): Transaction => {
    const isContractCall = tx.transaction_types.includes("contract_call");
    const ticker = tx.transaction_types.includes("coin_transfer") ? symbol : undefined;

    return {
      hash: tx.hash,
      from: tx.from.hash,
      to: tx.to.hash,
      amount: formatEthersAmount(tx.value),
      timestamp: tx.timestamp,
      explorerUrl: `${explorerUrl}/tx/${tx.hash}`,
      status: tx.status,
      fee: formatEthersAmount(tx.fee.value),
      chainId,
      id: tx.hash,
      txDirection: getTransactionDirection(tx, address),
      operationType: OperationTypes.TRANSACTION,
      ticker,
      isContractCall,
    };
  };

export const normalizeEvmNFT =
  (chainId: ChainFullId) =>
  (nft: AddressNFTInstance): MultichainNFTBalance => {
    const { token, id, value, token_type, metadata, image_url, media_url, animation_url } = nft;

    const defaultName = `${token.symbol || "NFT"} #${id}`;
    const name = metadata?.name || token.name || defaultName;
    const imageUrl = image_url || media_url || metadata?.image || token.icon_url;
    const animationUrl = animation_url || metadata?.animation_url;

    const attributes = metadata?.attributes?.map(attr => ({
      name: attr.trait_type,
      value: attr.value,
    }));

    const tokenAddress = token.address_hash || `unknown-${token.symbol || "NFT"}`;

    return {
      id: `${tokenAddress}-${id}`,
      address: tokenAddress,
      balance: value,
      chainId,
      type: token_type,
      imageUrl,
      name,
      token,
      animationUrl,
      tokenId: id,
      metadata: {
        ...(metadata ?? {}),
        attributes,
      },
    };
  };

export const normalizeEvmToken =
  (chainId: ChainFullId) =>
  (tokenBalance: TokenBalance): MultichainTokenBalance => {
    const { token } = tokenBalance;
    const decimals = parseInt(token.decimals, 10);
    const balance = formatUnits(tokenBalance.value, decimals);
    const tokenAddress = token.address_hash || `unknown-${token.symbol}`;

    return {
      id: `${tokenAddress}-${tokenBalance.token_id || "0"}`,
      address: tokenAddress,
      name: token.name,
      symbol: token.symbol,
      decimals,
      balance,
      value: tokenBalance.value,
      chainId,
      type: token.type,
      iconUrl: token.icon_url,
    };
  };

export const getIconUrl = (token: any): string | undefined => {
  if (!token) {
    return undefined;
  }

  if ("image_url" in token) {
    return token.image_url;
  }

  if ("media_url" in token) {
    return token.media_url as string;
  }

  if ("icon_url" in token) {
    return token.icon_url;
  }

  return token.metadata?.image;
};

export const normalizeEvmTokenTransfer =
  (chainId: ChainFullId, { explorerUrl }: { explorerUrl: string }) =>
  (tx: TokenTransfer, address: string): Transaction => {
    const { transaction_hash, from, to, total, timestamp, type, token: rawToken } = tx;

    const amount =
      "value" in total ? formatUnits(total.value, parseInt(total.decimals ?? "0", 10)) : "1";
    const token = "token_instance" in tx.total ? tx.total.token_instance : tx.token;
    const ticker =
      token && "metadata" in token && token.metadata && "name" in token.metadata
        ? (token.metadata.name as string)
        : rawToken.symbol;
    const iconUrl = getIconUrl(token);

    return {
      hash: transaction_hash,
      from: from.hash,
      to: to.hash,
      amount,
      timestamp,
      explorerUrl: `${explorerUrl}/tx/${transaction_hash}`,
      chainId,
      id: transaction_hash,
      txDirection: getTransactionDirection(tx, address),
      operationType: type as OperationType,
      ticker,
      token,
      iconUrl,
      isNft: rawToken.type === "ERC-721" || rawToken.type === "ERC-1155",
    };
  };

export const evmDerivationPathTemplate = "m/44'/60'/?'/0/0";

export const getChainConfig = <T extends Record<string, any>>(
  config: T,
  envKey: string,
  chainName: string
): T[keyof T] => {
  const configKey = getEnv(envKey) as keyof T;

  if (!(configKey in config)) {
    throw new Error(`Invalid ${chainName} network config: ${String(configKey)}.`);
  }

  return config[configKey];
};
